import React, { useState } from 'react';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { MetadataFieldDefinition } from '../generated-api';

interface MetadataFieldsEditorProps {
  fields: MetadataFieldDefinition[];
  onFieldsChange: (fields: MetadataFieldDefinition[]) => void;
}

const MetadataFieldsEditor: React.FC<MetadataFieldsEditorProps> = ({
  fields,
  onFieldsChange,
}) => {
  const [expanded, setExpanded] = useState(false);

  const addField = () => {
    const newField: MetadataFieldDefinition = {
      name: '',
      displayName: '',
      type: 'text',
      options: null,
    };
    onFieldsChange([...fields, newField]);
  };

  const removeField = (index: number) => {
    onFieldsChange(fields.filter((_, i) => i !== index));
  };

  const updateField = (index: number, updates: Partial<MetadataFieldDefinition>) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...updates };
    onFieldsChange(updated);
  };

  const addOption = (fieldIndex: number, option: string) => {
    const field = fields[fieldIndex];
    if (!field.options) {
      field.options = [];
    }
    if (!field.options.includes(option.trim())) {
      const updatedOptions = [...field.options, option.trim()];
      updateField(fieldIndex, { options: updatedOptions });
    }
  };

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const field = fields[fieldIndex];
    if (field.options) {
      const updatedOptions = field.options.filter((_, i) => i !== optionIndex);
      updateField(fieldIndex, { options: updatedOptions.length > 0 ? updatedOptions : null });
    }
  };

  const validateFieldName = (name: string): boolean => {
    // Alphanumeric + underscore only
    return /^[a-zA-Z0-9_]+$/.test(name);
  };

  return (
    <div className="border border-edge rounded-lg" data-testid="metadata-fields-editor">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-surface-hover transition-colors"
        data-testid="metadata-fields-editor-toggle"
      >
        <div>
          <span className="font-medium text-sm text-heading">Player Metadata Fields</span>
          <p className="text-xs text-muted mt-1">
            Define custom fields to track for each team in play sessions
          </p>
        </div>
        {expanded ? (
          <svg className="w-5 h-5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-edge pt-4">
          {fields.map((field, index) => (
            <div key={index} className="border border-edge rounded-lg p-4 space-y-3" data-testid={`metadata-field-${index}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  {/* Field Name */}
                  <div>
                    <label className="block text-xs font-medium text-body mb-1">
                      Field Name (identifier) *
                    </label>
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateFieldName(value) || value === '') {
                          updateField(index, { name: value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-edge-input rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., faction, strategy"
                      data-testid={`metadata-field-${index}-name`}
                    />
                    <p className="text-xs text-muted mt-1">
                      Alphanumeric and underscore only (used as key in metadata)
                    </p>
                  </div>

                  {/* Display Name */}
                  <div>
                    <label className="block text-xs font-medium text-body mb-1">
                      Display Name *
                    </label>
                    <input
                      type="text"
                      value={field.displayName}
                      onChange={(e) => updateField(index, { displayName: e.target.value })}
                      maxLength={50}
                      className="w-full px-3 py-2 border border-edge-input rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Faction, Strategy"
                      data-testid={`metadata-field-${index}-display-name`}
                    />
                  </div>

                  {/* Field Type */}
                  <div>
                    <label className="block text-xs font-medium text-body mb-1">
                      Field Type *
                    </label>
                    <select
                      value={field.type}
                      onChange={(e) => {
                        const newType = e.target.value as 'text' | 'number' | 'boolean' | 'select' | 'multiselect' | 'order';
                        // Preserve options for text, select, and multiselect; clear for number, boolean, and order
                        updateField(index, {
                          type: newType,
                          options: (newType === 'text' || newType === 'select' || newType === 'multiselect') 
                            ? (field.options || []) 
                            : null,
                        });
                      }}
                      className="w-full px-3 py-2 border border-edge-input rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      data-testid={`metadata-field-${index}-type`}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean (Yes/No)</option>
                      <option value="select">Select (Single Choice)</option>
                      <option value="multiselect">Multiselect (Multiple Choice)</option>
                      <option value="order">Order (Team Position)</option>
                    </select>
                  </div>

                  {/* Options (for text, select, and multiselect fields) */}
                  {(field.type === 'text' || field.type === 'select' || field.type === 'multiselect') && (
                    <div>
                      <label className="block text-xs font-medium text-body mb-1">
                        {field.type === 'text' ? 'Predefined Options (Optional)' : 'Options (Required)'}
                      </label>
                      <p className="text-xs text-muted mb-2">
                        {field.type === 'text' 
                          ? 'Add common values for autocomplete. Users can still enter custom values.'
                          : field.type === 'select'
                          ? 'Define the choices users can select from. At least one option is required.'
                          : 'Define the choices users can select from. Users can select multiple options.'}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {field.options?.map((option, optIndex) => (
                          <span
                            key={optIndex}
                            className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                          >
                            {option}
                            <button
                              data-track="metadata_option_remove"
                              type="button"
                              onClick={() => removeOption(index, optIndex)}
                              className="ml-1 text-primary-600 hover:text-primary-900"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const input = e.currentTarget;
                              if (input.value.trim()) {
                                addOption(index, input.value);
                                input.value = '';
                              }
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-edge-input rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Add option and press Enter"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                            if (input.value.trim()) {
                              addOption(index, input.value);
                              input.value = '';
                            }
                          }}
                          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delete Button */}
                <button
                  type="button"
                  onClick={() => removeField(index)}
                  className="ml-4 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  title="Remove field"
                  data-testid={`metadata-field-${index}-remove`}
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addField}
            className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-edge-input rounded-md text-sm font-medium text-body hover:border-primary-400 hover:text-primary-600 transition-colors"
            data-testid="metadata-field-add-button"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Field
          </button>

          {fields.length === 0 && (
            <p className="text-sm text-muted text-center py-4">
              No metadata fields defined. Add fields to track custom information for each team.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MetadataFieldsEditor;

